import { Icon } from "@twinkltech/curriculum-architect-component-library";
import { Button } from "@twinkltech/web-ui";
import { useRef, useState, useEffect, useId } from "react";
import { useTranslation } from "react-i18next";

import { COMMON_NS } from "@/i18n/common/common.i18n";

export const DeleteButton = ({
  disabled,
  onDelete,
}: {
  onDelete: () => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const buttonLabelId = useId();
  const statusId = useId();

  // the the pages focussed element is not the deleteButtonRef button, then we want to reset the isConfirming state
  const handleExternalFocus = (e: FocusEvent) => {
    if (deleteButtonRef.current && e.target !== deleteButtonRef.current) {
      setIsConfirming(false);
    }
  };

  // add event listener to the document to listen for any click or focus events outside of the confirm button
  // and clean up the event listener on unmount
  useEffect(() => {
    document.addEventListener("click", handleExternalFocus);
    document.addEventListener("focusin", handleExternalFocus);
    return () => {
      document.removeEventListener("click", handleExternalFocus);
      document.removeEventListener("focusin", handleExternalFocus);
    };
  }, []);

  return (
    <>
      <div
        aria-atomic="true"
        aria-live="assertive"
        className="sr-only"
        id={statusId}
        role="status"
      >
        {isConfirming
          ? t("actions.confirmDeletePrompt", {
              ns: COMMON_NS,
              defaultValue: "Please confirm if you want to delete",
            })
          : ""}
      </div>
      <Button
        ref={deleteButtonRef}
        aria-describedby={isConfirming ? statusId : undefined}
        aria-expanded={isConfirming}
        aria-labelledby={buttonLabelId}
        className={isConfirming ? undefined : "text-red-500"}
        disabled={disabled}
        type="button"
        variant={isConfirming ? "error" : "ghost"}
        onClick={isConfirming ? onDelete : () => setIsConfirming(true)}
      >
        <Icon className="min-w-[24px]" icon="DoNotDisturbOnFilled" />
        {isConfirming && t("actions.delete", { ns: COMMON_NS })}

        <span className="sr-only" id={buttonLabelId}>
          {isConfirming
            ? t("actions.confirmDelete", {
                defaultValue: "Confirm delete",
                ns: COMMON_NS,
              })
            : t("actions.delete", { ns: COMMON_NS })}
        </span>
      </Button>
    </>
  );
};
